'use client';

import { useMutation } from '@tanstack/react-query';
import type { CreateCredentialDto } from '@terra/shared-contracts-applications';
import { terraProxyApiClient } from '../../clients/testerClient';
import { useCopilotSettingsRefreshStore } from '../../store/copilotSettingsRefreshStore';
import { useInvalidateApplicationCredentials } from './useDecryptedApplicationCredentials';

function useCredentialsInvalidation() {
  const invalidateApplicationCredentials =
    useInvalidateApplicationCredentials();

  return async (applicationId: string) => {
    useCopilotSettingsRefreshStore
      .getState()
      .markSettingsRefreshFromRoleCredentialQueues();
    await invalidateApplicationCredentials(applicationId);
  };
}

export function useSaveApplicationCredential() {
  const invalidate = useCredentialsInvalidation();

  return useMutation({
    mutationFn: (variables: {
      applicationId: string;
      body: CreateCredentialDto;
    }) => terraProxyApiClient.saveCredential(variables),
    onSuccess: (_result, { applicationId }) => invalidate(applicationId),
  });
}

export function useDeleteApplicationCredential() {
  const invalidate = useCredentialsInvalidation();

  return useMutation({
    mutationFn: (variables: { applicationId: string; credentialId: string }) =>
      terraProxyApiClient.deleteCredential(variables),
    onSuccess: (_result, { applicationId }) => invalidate(applicationId),
  });
}

export function useToggleApplicationCredentialStatus() {
  const invalidate = useCredentialsInvalidation();

  return useMutation({
    mutationFn: (variables: {
      applicationId: string;
      credentialId: string;
      isDisabled: boolean;
    }) => terraProxyApiClient.toggleCredentialStatus(variables),
    onSuccess: (_result, { applicationId }) => invalidate(applicationId),
  });
}

export function useVerifyApplicationCredential() {
  const invalidate = useCredentialsInvalidation();

  return useMutation({
    mutationFn: (variables: {
      applicationId: string;
      credentialId: string;
      streamingId: string;
    }) => terraProxyApiClient.verifySingleCredential(variables),
    onSuccess: (_result, { applicationId }) => invalidate(applicationId),
  });
}

export function useVerifyAllApplicationCredentials() {
  const invalidate = useCredentialsInvalidation();

  return useMutation({
    mutationFn: ({
      applicationId,
      streamingIds,
    }: {
      applicationId: string;
      streamingIds?: Record<string, string>;
    }) => terraProxyApiClient.verifyCredentials(applicationId, streamingIds),
    onSuccess: (_result, { applicationId }) => invalidate(applicationId),
  });
}

export function useAbortApplicationCredentialVerification() {
  const invalidate = useCredentialsInvalidation();

  return useMutation({
    mutationFn: (variables: { applicationId: string; credentialId: string }) =>
      terraProxyApiClient.abortCredentialVerification(variables),
    onSuccess: (_result, { applicationId }) => invalidate(applicationId),
  });
}
